import { createFileRoute } from "@tanstack/react-router";
import { CategoryLanding, categoryHead, productsQueryOptions } from "@/components/CategoryLanding";

// Broad head-term landing for "lastenhuoneen kalusteet". Also aliased
// against "lastenhuoneen sisustus", "lasten kalusteet", "puiset lasten
// kalusteet". Aggregates all furniture-like products across categories,
// giving Google a canonical topical hub distinct from /kauppa.
const SLUG = "lastenhuoneen-kalusteet";
const H1 = "Lastenhuoneen kalusteet — Montessori-huonekalut lapselle koivuvanerista";
const TITLE = "Lastenhuoneen kalusteet — Montessori-huonekalut | Montessori24";
const DESC =
  "Lastenhuoneen kalusteet Montessori-tyyliin: oppimistorni, Pikler-kolmio, lasten pöytä ja tuoli, matala hylly, vaaterekki ja lattiapatja. Koivuvaneri, ilmainen toimitus 3–7 pv.";

const FAQS = [
  {
    q: "Mitä kalusteita lastenhuoneeseen kannattaa hankkia ensin?",
    a: "Neljä perusasiaa: 1) Lattiapatja tai matala sänky, jolta lapsi pääsee itse alas. 2) Matala 2-tasoinen leluhylly, jossa on kerrallaan 5–6 valittua esinettä. 3) Oppimistorni keittiöön, jotta lapsi pääsee osallistumaan arjen puuhiin. 4) Lasten pöytä ja tuoli, jonka äärellä lapsi voi piirtää ja syödä itsenäisesti. Näillä pärjää 1–5 vuoden ajan.",
  },
  {
    q: "Miten Montessori-huonekalut eroavat tavallisista lastenkalusteista?",
    a: "Kolme perusperiaatetta: kalusteet ovat lapsen mittakaavassa (ei aikuiskorkeudessa), materiaali on aitoa (koivuvaneri, ei muovia tai kirsikoristeita), ja jokainen kaluste tukee itsenäisyyttä. Matala hylly, lattiapatja ja oppimistorni ovat ikoniesimerkkejä — lapsi voi käyttää niitä ilman aikuisen apua.",
  },
  {
    q: "Kestävätkö lasten koivuvanerikalusteet pitkään?",
    a: "Koivuvaneri kestää 10–15 vuoden käytön, kun pinta on lakattu vesiohenteisella lapsiturvallisella lakalla. Vertailun vuoksi: muovinen lastenpöytä kestää yleensä 1–2 vuotta ja päätyy kaatopaikalle. Yksi laadukas puukaluste korvaa 3–5 muovihankintaa.",
  },
  {
    q: "Mistä puusta lastenhuoneen kalusteet valmistetaan?",
    a: "Meidän kalusteemme tehdään suomalaisesta ja baltialaisesta koivuvanerista, joka on kestävää, kevyttä ja pinnaltaan sileää. Pintakäsittelynä on tyypillisesti vesiohenteinen lakka tai luonnonöljy. Tuotekohtaiset materiaali- ja turvallisuustiedot ilmoitetaan tuotesivulla.",
  },
  {
    q: "Sopivatko Montessori-kalusteet pieneen lastenhuoneeseen?",
    a: "Sopivat erinomaisesti — Montessori-huonekalut ovat lapsen mittakaavassa, siis pienempiä kuin aikuiskalusteet. Pikler-kolmio taittuu 15 cm paksuiseksi sängyn alle. Oppimistorni vie noin 40×50 cm lattiatilaa. Matala hylly korvaa isot kaapit. Kokonaisuus on yleensä tilatehokkaampi kuin perinteinen lastenhuoneen kalustus.",
  },
  {
    q: "Voiko lastenhuoneen kalustaa Montessori-tyyliin vaiheittain?",
    a: "Kyllä ja kannattaakin. Aloita yhdellä kalusteella (esim. oppimistorni tai matala hylly), katso 1–2 kuukautta miten lapsi käyttää sitä, ja lisää seuraava. Kerralla koko huoneen kalustaminen on kallista, mutta myös vie sinulta mahdollisuuden havainnoida, mikä oikeasti toimii omalla lapsellasi.",
  },
];

export const Route = createFileRoute("/lastenhuoneen-kalusteet")({
  loader: ({ context }) => context.queryClient.ensureQueryData(productsQueryOptions),
  head: () => categoryHead({ slug: SLUG, title: TITLE, h1: H1, description: DESC, faqs: FAQS }),
  component: LastenhuoneenKalusteetPage,
});

function LastenhuoneenKalusteetPage() {
  return (
    <CategoryLanding
      slug={SLUG}
      eyebrow="Lastenhuoneen kalusteet"
      title={H1}
      intro="Lastenhuoneen kalusteet Montessori-periaatteilla: lapsen mittakaavassa, koivuvanerista ja rakennettu tukemaan itsenäisyyttä. Oppimistornit, Pikler-kolmiot, matalat pöydät, vaaterekit ja säilytyskalusteet — yksi hankinta palvelee vuosia."
      kauppaCategory="all"
      match={(t) =>
        /torni|apujakkar|jakkar|kiipeil|kolmio|kaari|ramp|pöyt|tuoli|keinutuoli|hylly|kirja|vaaterekki|vaateteline|säilyt|laatikko|kirjateline|kirjanäyttö|lastenhyllykk|patja|s[aä]nky|helper tower|kitchen tower|tower|stool|table|chair|book|shelf|storage|clothing rack|bed|mattress|climbing|climber|triangle|arch/i.test(t)
      }
      relatedChips={[
        { label: "Oppimistorni", to: "/oppimistorni" },
        { label: "Pikler-kolmio", to: "/pikler-kolmio" },
        { label: "Vaaterekki", to: "/vaaterekki" },
        { label: "Montessori-sänky", to: "/montessori-sanky" },
        { label: "Kalusteopas", to: "/montessori-koulu/montessori-kalusteet-kotiin" },
      ]}
      body={[
        {
          heading: "Neljä kalustetta joilla pärjää 0–6-vuotiaan lastenhuoneessa",
          paragraphs: [
            "Matala sänky tai lattiapatja: lapsi pääsee itse nukkumaan ja herää itse. Ei häkkiä, ei kompromissia turvallisuudesta — vain matala korkeus ja pehmeä alusta.",
            "Matala leluhylly: 2 tasoa, 5–6 esinettä kerrallaan. Lapsi valitsee, käyttää ja palauttaa itse. Vaihda esineitä 1–2 viikon välein — vähemmän on enemmän.",
            "Oppimistorni keittiöön: 12 kk taapero pääsee työtason korkeudelle turvallisesti. Osallistuu ruoanlaittoon vanhemman rinnalla — ei enää istumista lattialla katsomassa.",
            "Lasten pöytä ja tuoli: 40 cm korkuinen pöytä ja 20 cm tuoli. Lapsen tasolla lapsen kokoinen. Piirtämistä, palapelejä, aamiaista — itsenäisesti.",
          ],
        },
        {
          heading: "Miksi koivuvaneri on paras materiaali lastenhuoneen kalusteisiin",
          paragraphs: [
            "Koivuvaneri on kevyttä (lapsi voi siirtää tuolin itse), kestävää (kestää 10–15 vuoden käytön), turvallista (lapsiturvallinen pintakäsittely) ja kaunista (luonnollinen syy). Muovi ei kestä eikä tunnu, MDF-levyt haisevat vuosia ja lakattu massiivipuu on painava.",
            "Valikoimamme kalusteet ovat pääosin koivuvaneria. Pinnat on käsitelty vesiohenteisella lakalla tai luonnonöljyllä. Tuotekohtaiset materiaali- ja turvallisuustiedot löydät jokaiselta tuotesivulta.",
          ],
        },
        {
          heading: "Lastenhuoneen sisustus Montessori-tyyliin — mistä aloittaa",
          paragraphs: [
            "Aloita siltä, mikä lasta juuri nyt turhauttaa. Jos taapero yrittää päästä työtasolle jakkaralla — hanki oppimistorni. Jos leluja on kaikkialla — hanki matala hylly ja karsi. Jos itsenäinen pukeutuminen ei suju — hanki lasten vaaterekki.",
            "Kalliit kokoremontit harvoin toimivat, koska et voi ennustaa mihin lapsi tarttuu. Sen sijaan yksi hyvin valittu kaluste kerrallaan opettaa sinut näkemään, mitä lapsi oikeasti tarvitsee — ja lopputulos on käytännöllisempi kuin Pinterest-inspiroitu kertaostoksissa tehty huone.",
          ],
        },
      ]}
      faqs={FAQS}
      cta={{
        heading: "Rakenna Montessori-lastenhuone askel askeleelta",
        body: "Koivuvaneri, ilmainen toimitus 3–7 päivässä. 30 päivän palautusetu.",
        primaryLabel: "Selaa kaikkia kalusteita",
        secondaryLabel: "Lue kalusteopas",
        secondaryTo: "/montessori-koulu/montessori-kalusteet-kotiin",
      }}
    />
  );
}